import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutGrid, Plus, MessagesSquare, BarChart3 } from "lucide-react";

export default function DealerLayout() {
  const { user } = useAuth();
  const loc = useLocation();
  const [stats, setStats] = useState(null);
  const isPrivate = user?.role === "private";
  const privateLimitReached = isPrivate && stats?.max_private_listings && stats.total_listings >= stats.max_private_listings;

  useEffect(() => {
    api.get("/dealer/stats").then(r => setStats(r.data)).catch(() => {});
  }, [loc.pathname]);

  return (
    <div className="page-wrap py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="swiss-label text-[#16A34A]">{isPrivate ? "Privatbereich" : "Händler-Dashboard"}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Hallo, {user?.name?.split(" ")[0]}</h1>
          {isPrivate && stats && (
            <p className="text-sm text-gray-500 mt-2">
              Private Nutzer können maximal {stats.max_private_listings} Anzeigen aufgeben. Aktuell: {stats.total_listings} / {stats.max_private_listings}.
            </p>
          )}
        </div>
        {privateLimitReached ? (
          <button disabled data-testid="new-listing-btn-disabled" className="swiss-btn-secondary opacity-50 cursor-not-allowed">
            <Plus className="w-4 h-4" /> Limit erreicht
          </button>
        ) : (
          <Link to="/dashboard/neu" data-testid="new-listing-btn" className="swiss-btn-primary"><Plus className="w-4 h-4" /> Neues Fahrzeug</Link>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label="Inserate gesamt" value={stats.total_listings} />
          <StatCard label="Veröffentlicht" value={stats.published_listings} accent />
          <StatCard label="Anfragen gesamt" value={stats.total_inquiries} />
          <StatCard label="Ungelesen" value={stats.unread_inquiries} highlight={stats.unread_inquiries > 0} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside>
          <nav className="space-y-1">
            <SideLink to="/dashboard" end icon={LayoutGrid} label="Inserate" testid="dash-nav-listings" />
            <SideLink to="/dashboard/anfragen" icon={MessagesSquare} label="Anfragen" testid="dash-nav-inquiries" />
            <SideLink to="/dashboard/stats" icon={BarChart3} label="Analytics" testid="dash-nav-stats" />
          </nav>
        </aside>
        <section>
          <Outlet context={{ refreshStats: () => api.get("/dealer/stats").then(r => setStats(r.data)) }} />
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, highlight }) {
  return (
    <div className={`swiss-card p-5 ${highlight ? "border-[#16A34A] border-2" : ""}`}>
      <div className="swiss-label text-gray-500">{label}</div>
      <div className={`font-display font-extrabold text-3xl mt-2 ${accent ? "text-[#16A34A]" : ""}`}>{value}</div>
    </div>
  );
}

function SideLink({ to, end, icon: Icon, label, testid }) {
  return (
    <NavLink end={end} to={to} data-testid={testid} className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 text-sm font-semibold border-l-4 transition-colors ${isActive ? "bg-gray-50 border-[#16A34A] text-[#0A0A0A]" : "border-transparent text-gray-600 hover:text-[#0A0A0A] hover:bg-gray-50"}`
    }>
      <Icon className="w-4 h-4" /> {label}
    </NavLink>
  );
}
